import React, { useState, useEffect } from 'react';
import {
  Button, Box, Typography, CircularProgress,
} from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import MfaLayout from '../../components/MFA/MfaLayout';
import { enrollMfa } from '../../services/mfaService';

const MfaEnrollment = () => {
  const history = useHistory();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [error, setError] = useState(false);

  const mfaToken = location.state?.mfaToken;

  useEffect(() => {
    if (!mfaToken) {
      history.push('/login');
      return;
    }
    const initEnrollment = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await enrollMfa(mfaToken);
        setQrCodeData(response);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    initEnrollment();
  }, [mfaToken, history]);

  const handleContinue = () => {
    if (!qrCodeData) return;
    history.push('/mfa/verify', { mfaToken, mode: 'enrollment' });
  };

  const handleRetry = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <MfaLayout title="CONNEXION" subtitle="Chargement en cours...">
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </MfaLayout>
    );
  }

  if (error || !qrCodeData) {
    return (
      <MfaLayout
        title="CONNEXION"
        subtitle="Erreur lors du chargement. Veuillez réessayer."
        hasError
      >
        <Box marginTop="30px" width="100%">
          <Button
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            onClick={handleRetry}
          >
            Réessayer
          </Button>
        </Box>
      </MfaLayout>
    );
  }

  return (
    <MfaLayout
      title="CONNEXION"
      subtitle="Configuration de l'authentification à deux facteurs."
      subtitle2="L'authentification à deux facteurs ajoute une couche de sécurité supplémentaire à votre compte"
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 6,
          width: '100%',
        }}
      >
        {/* Steps */}
        <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[
            {
              number: 1,
              text: (
                <>
                  Téléchargez{' '}
                  <span style={{ fontWeight: 700, color: '#333' }}>Google Authenticator</span>{' '}
                  depuis l&apos;
                  <a
                    href="https://apps.apple.com/app/google-authenticator/id388497605"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1976d2', fontWeight: 600 }}
                  >
                    App Store
                  </a>{' '}
                  ou{' '}
                  <a
                    href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1976d2', fontWeight: 600 }}
                  >
                    Google Play Store
                  </a>
                </>
              ),
            },
            { number: 2, text: 'Scannez ce code QR avec votre téléphone' },
            {
              number: 3,
              text: (
                <>
                  Cliquez sur{' '}
                  <span style={{ fontWeight: 700, color: '#1976d2' }}>&quot;Continuer&quot;</span>{' '}
                  pour confirmer la configuration
                </>
              ),
            },
          ].map((step) => (
            <Box
              key={step.number}
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                px: 3,
                py: 2,
                minHeight: 50,
                width: '100%',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#1976d2',
                  fontSize: 32,
                  minWidth: 50,
                  textAlign: 'center',
                  mr: 4,
                  lineHeight: 1,
                }}
              >
                {step.number}
              </Typography>
              <Typography
                sx={{ fontSize: 16, color: '#333', fontWeight: 500, lineHeight: 1.5, flex: 1 }}
                component="span"
              >
                {step.text}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* QR Code */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 240,
          }}
        >
          {qrCodeData.qrCodeDataUrl ? (
            <img
              src={qrCodeData.qrCodeDataUrl}
              alt="QR Code pour l'authentification"
              style={{
                width: '240px',
                height: '240px',
                display: 'block',
                objectFit: 'contain',
                aspectRatio: '1 / 1',
              }}
            />
          ) : (
            <Typography color="error">QR Code non disponible</Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{
            minWidth: 300,
            fontWeight: 700,
            fontSize: 18,
            borderRadius: '8px',
            py: 1.5,
            textTransform: 'none',
          }}
          onClick={handleContinue}
        >
          CONTINUER
        </Button>
      </Box>
    </MfaLayout>
  );
};

export default MfaEnrollment;
